<h2 class="sectionHeader">Using Dash with Google</h2>

<p class="intro">
  Before connecting Dash to your Google account you need to make sure that your meeting rooms are set up and
  managed in your Google Workspace Organization, and that you've created and set up a Google account for using with Dash.
</p>

<h3>Setting up Google Workspace</h3>
<p>
  We recommend that you set up each of your meeting rooms as a <a href="https://support.google.com/a/answer/1033925?hl=en">
  Resource</a>.  Resources set up in this way will appear under a list of "Rooms or resources" when you are creating a
  meeting, and support advanced features such as automatically declining meeting invitations when there are clashes
  and double bookings.  They are also automatically shared with all user accounts in your Google organization.
</p>

<p>
  If you do not wish to use Resources, it is also possible to set up each meeting room as a
  <a href="https://support.google.com/a/users/answer/37082?hl=en">
    Shared calendar</a>.  Shared Calendars do not show up as resources when creating a meeting, and do not automatically decline
  bookings if there is a conflict.  They also require you to share each calendar individually with every user who
  needs to book meetings in the room, so using Resources is highly recommended.
</p>

<h3>Choose a connection method</h3>
<p>
  There are two methods that you can use to connect Dash to Google: a service account, and a standard or personal account. Both offer their own advantages and tradeoffs:</p>

<div class="connectionMethod">
  <h4>Google Workspace Service account</h4>
  <p>A service account is the recommended way to connect Dash to Google Workspace, and allows Dash to connect on behalf
    of your entire organization.  This method must be set up by a global administrator in your Google Workspace admin
    console, and Google Developer console. Dash will automatically see all of your resource calendars without you having
    to individually share them with Dash, and Dash has access to your user directory, so you can add attendees and set
    the organizer of meetings booked from your Dash displays.</p>
  <ul>
    <li>Your meeting rooms must be set up as resource calendars</li>
    <li>Does not require calendars to be individually shared with Dash</li>
    <li>Requires Google Workspace (does not work for personal Google accounts)</li>
    <li>Dash can integrate with your Google Workspace user directory</li>
  </ul>
  <p class="tip">For help setting up Dash to use a service account, see our <a routerLink="../google-service-account">online guide</a>.</p>
</div>

<div class="connectionMethod">
  <h4>Google Workspace standard, or personal account</h4>
  <p>Sign into your Google Workspace or personal account with Dash.  This is the easiest method to set up as you just
    have to sign in, and it supports personal Google accounts.  You'll need to share each meeting room calendar with the
    account you choose to use with Dash, so that dash can see and book meetings in your organization.
  </p>
  <ul>
    <li>You'll need to share each meeting room calendar with Dash separately</li>
    <li>Supports personal Google accounts</li>
    <li>Does not require access to the Google Developer Console</li>
    <li>Dash cannot integrate with your Google Workspace user directory</li>
  </ul>

  <p class="tip">For help setting up Dash to connect to Google via a standard or personal account, see our
    <a routerLink="../google-personal-account">online guide</a>.</p>
</div>


